import { resolveHtmlVideo } from './resolvers/html';
import { resolveYoutubePoster, resolveYoutubeVideo } from './resolvers/youtube';

import type {
  VideoService,
  VideoResolver,
  PosterResolver,
  VideoResolverReturnValue,
  NativeVideoResolverReturnValue,
  IframeVideoResolverReturnValue,
} from './types';

export const resolveVideo: VideoResolver = args => {
  const resolved = resolveVideoInternal(args);

  if (typeof resolved === 'undefined') {
    return resolved;
  }

  if (typeof args.postResolveVideo !== 'undefined') {
    return args.postResolveVideo(args, resolved);
  }

  return resolved;
};

const resolveVideoInternal: VideoResolver = args => {
  if (typeof args.src !== 'string') {
    return undefined;
  }

  const service = getVideoService(args.src);

  if (service === 'html') {
    return resolveHtmlVideo(args);
  }

  if (service === 'youtube') {
    return resolveYoutubeVideo(args);
  }

  return undefined;
};

export const resolvePoster: PosterResolver = args => {
  if (args.poster) {
    return args.poster;
  }

  if (typeof args.src !== 'string') {
    return undefined;
  }

  const service = getVideoService(args.src);

  if (service === 'youtube') {
    return resolveYoutubePoster(args);
  }

  return undefined;
};

export const resolvedVideoIsNative = (
  data: VideoResolverReturnValue | undefined,
): data is NativeVideoResolverReturnValue => {
  return typeof data !== 'undefined' && 'video' in data;
};

export const resolvedVideoIsIframe = (
  data: VideoResolverReturnValue | undefined,
): data is IframeVideoResolverReturnValue => {
  return typeof data !== 'undefined' && 'iframe' in data;
};

export const getVideoService = (src: string): VideoService | undefined => {
  if (typeof src === 'undefined') {
    return undefined;
  }

  if (
    src.includes('youtube.com') ||
    src.includes('youtu.be') ||
    src.includes('youtube-nocookie.com')
  ) {
    return 'youtube';
  }

  if (src.includes('http:') || src.includes('https:') || src.includes('/')) {
    return 'html';
  }

  return undefined;
};

export const getVideoId = (
  service: VideoService,
  src: string,
): string | undefined => {
  if (service === 'youtube') {
    const pattern =
      /^.*(?:(?:youtu\.be\/|v\/|vi\/|u\/\w\/|embed\/|shorts\/|live\/)|(?:(?:watch)?\?v(?:i)?=|&v(?:i)?=))([^#&?]*).*/;

    const id = src.match(pattern)?.[1] ?? '';

    return id !== '' ? id : undefined;
  }

  return undefined;
};
